import React, { useState, SetStateAction, Dispatch } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  Button,
  Divider,
  // Dialog,
  // DialogTitle,
  // DialogContent,
  TextField,
  Select,
  Pagination,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
// import CloseIcon from "@mui/icons-material/Close";

import { IAccount } from "@/pages/Account";
import { apiClient } from "@/api";
import { useRecoilValue } from "recoil";
import { accessTokenState } from "@/client/state";

import PreviewIcon from "@/assets/preview";
import DeleteAccount from "../DeleteAccount"; // Import the new delete dialog

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Menu from "@mui/material/Menu";

import { StatusChip } from "@/components/atoms/StatusChip";

import { useSetRecoilState } from "recoil";
import { companyState, currentThreadIdState } from "@/client/state";
import { useChatInteract } from "@/client/useChatInteractV2";
type Filters = {
  search: string;
  sort_order: string;
  sort_by: string;
  page: number;
  limit: number;
};

interface Props {
  accounts?: IAccount[];
  error?: string;
  isLoading: boolean;
  onAccountUpdated: () => void;
  paging?: number;
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
}

const AccountTable: React.FC<Props> = ({
  accounts,
  error,
  isLoading,
  onAccountUpdated,
  paging,
  filters,
  setFilters,
}) => {
  const accessToken = useRecoilValue(accessTokenState);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortKey, setSortKey] = useState<"company_name" | "created_at">(
    "created_at"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [openDialog, setOpenDialog] = useState<"" | "file" | "delete">("");
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null
  );
  const setCompanyState = useSetRecoilState(companyState);
  const setThreadState = useSetRecoilState(currentThreadIdState);

  const { clear } = useChatInteract();

  // const [openFilesModal, setOpenFilesModal] = useState(false);
  // const [selectedAccountFiles, setSelectedAccountFiles] =
  //   useState<IAccount | null>(null);

  const navigate = useNavigate();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  // const handleFilterChange = (e: SelectChangeEvent<string>) => {
  //   setFileTypeFilter(e.target.value);
  //   setFilters((prev) => ({ ...prev, search_by: e.target.value }));
  // };

  const handleClose = () => {
    setOpenDialog("");
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedAccountId !== null) {
      try {
        await apiClient.deleteAccount(selectedAccountId, accessToken);
        onAccountUpdated();
      } catch (error) {
        console.error("Delete file failed for " + selectedAccountId, error);
      }
    }
    setOpenDialog("");
    setSelectedAccountId(null);
  };

  return (
    <Stack sx={{ width: "100%", p: "36px 24px" }} direction="column" gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography
            sx={{ color: "text.primary", fontWeight: 700 }}
            variant="h6"
          >
            List Company
          </Typography>
          <Typography sx={{ color: "text.disabled" }} variant="subtitle2">
            List of companies that have registered and uploaded their
            application forms
          </Typography>
        </Stack>
        <Button
          id="confirm"
          autoFocus
          onClick={() => {
            setThreadState(null);
            navigate("/list-account/new");
          }}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            color: "secondary.contrastText",
            width: "160px",
            py: 1,
          }}
        >
          + Add Company
        </Button>
      </Stack>

      {isLoading && <>Loading...</>}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {/* Left: Search */}

        <TextField
          type="text"
          placeholder="Search by company name"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: 250 }}
        />

        {/* Right: Sort controls */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Select
            value={sortKey}
            onChange={(e) => {
              const key = e.target.value as "company_name" | "created_at";
              setSortKey(key);
              setFilters((prev) => ({ ...prev, sort_by: key, page: 1 }));
            }}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="company_name">Sort by Company Name</MenuItem>
            <MenuItem value="created_at">Sort by Date</MenuItem>
          </Select>

          <Select
            value={sortOrder}
            onChange={(e) => {
              const order = e.target.value as "asc" | "desc";
              setSortOrder(order);
              setFilters((prev) => ({ ...prev, sort_order: order, page: 1 }));
            }}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </Stack>
      </Stack>

      {!error &&
        !isLoading &&
        (!accounts || accounts.length === 0 ? (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 8 }}
          >
            <img
              src="/no_files.svg"
              alt="No Companies"
              style={{ maxWidth: "220px", marginBottom: "16px" }}
            />

            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              No Companies Yet
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              maxWidth={300}
            >
              Once a company registers and uploads their application form, it
              will appear here
            </Typography>

            <Button
              id="confirm"
              autoFocus
              onClick={() => {
                setThreadState(null);
                navigate("/list-account/new");
              }}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                // color: "secondary.contrastText",
                width: "160px",
                py: 1,
                mt: 2,
              }}
            >
              + Add Company
            </Button>
          </Stack>
        ) : (
          <TableContainer>
            <Table sx={{ borderCollapse: "separate", borderSpacing: "0 8px" }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID Account</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Created Date</TableCell>
                  {/* <TableCell align="center">Information Source</TableCell> */}
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Open Chat</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.company_name}</TableCell>
                    <TableCell>
                      {new Intl.DateTimeFormat().format(
                        new Date(row.created_at)
                      )}
                    </TableCell>
                    {/* <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedAccountFiles(row);
                        setOpenFilesModal(true);
                      }}
                    >
                      View Files
                    </Button>
                  </TableCell> */}

                    <TableCell align="center">
                      <StatusChip status={row.status} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        aria-label="preview"
                        size="small"
                        onClick={() => {
                          setCompanyState(String(row.id));
                          clear();
                          navigate("/");
                        }}
                      >
                        <PreviewIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={(e) => handleMenuOpen(e, row.id)}>
                        <MoreHorizIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}

      {/* <UploadFile
        open={openDialog === "file"}
        handleClose={handleClose}
        onAccountUpdated={onAccountUpdated}
      /> */}

      {!isLoading && paging && accounts?.length !== 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={1}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="page-size-label">Rows per page</InputLabel>
            <Select
              labelId="page-size-label"
              value={filters.limit.toString()}
              label="Rows per page"
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  limit: parseInt(e.target.value, 10),
                  page: 1,
                }))
              }
            >
              {[5, 10, 20, 50].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Pagination
            count={paging}
            page={filters.page}
            onChange={(_, value) =>
              setFilters((prev) => ({ ...prev, page: value }))
            }
            color="primary"
            variant="outlined"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </Stack>
      )}

      <DeleteAccount
        open={openDialog === "delete"}
        handleClose={handleClose}
        handleConfirmDelete={handleConfirmDelete}
        accountName={
          accounts?.find((account) => account.id === selectedAccountId)
            ?.company_name
        }
      />

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disabled
          value=""
          sx={{
            minWidth: 150,
            borderRadius: "25px",
          }}
        >
          Actions
        </MenuItem>
        <Divider sx={{ opacity: 0.3 }} />
        <MenuItem
          onClick={() => {
            navigate(`/list-account/edit/${menuRowId}`);
            handleMenuClose();
          }}
        >
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            setOpenDialog("delete");
            setSelectedAccountId(menuRowId);
            handleMenuClose();
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* View Files Modal */}
      {/* {selectedAccountFiles && (
        <Dialog
          open={openFilesModal}
          onClose={() => setOpenFilesModal(false)}
          PaperProps={{
            sx: { minWidth: 650 },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: "1.2rem",
              pr: 5, // extra right padding so the X doesn’t overlap text
            }}
          >
            Information Source for {selectedAccountFiles.company_name}
            <IconButton
              aria-label="close"
              onClick={() => setOpenFilesModal(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider sx={{ opacity: 0.3 }} />

          <DialogContent>
            <ul
              style={{
                paddingLeft: "1.5rem",
                listStyleType: "disc",
                marginBottom: "10px",
              }}
            >
              {(selectedAccountFiles.application_form_filename ||
                selectedAccountFiles.application_form_crawling) && (
                <li>
                  Application Form:
                  {selectedAccountFiles.application_form_filename}
                  {selectedAccountFiles.application_form_filename &&
                    selectedAccountFiles.application_form_crawling &&
                    " - "}
                  {selectedAccountFiles.application_form_crawling}
                </li>
              )}

              {(selectedAccountFiles.company_profile_filename ||
                selectedAccountFiles.company_profile_crawling) && (
                <li>
                  Company Profile:
                  {selectedAccountFiles.company_profile_filename}
                  {selectedAccountFiles.company_profile_filename &&
                    selectedAccountFiles.company_profile_crawling &&
                    " - "}
                  {selectedAccountFiles.company_profile_crawling}
                </li>
              )}

              {(selectedAccountFiles.financial_statement_filename ||
                selectedAccountFiles.financial_statement_crawling) && (
                <li>
                  Financial Statement:
                  {selectedAccountFiles.financial_statement_filename}
                  {selectedAccountFiles.financial_statement_filename &&
                    selectedAccountFiles.financial_statement_crawling &&
                    " - "}
                  {selectedAccountFiles.financial_statement_crawling}
                </li>
              )}
              {selectedAccountFiles.other_file_filename && (
                <li>{selectedAccountFiles.other_file_filename}</li>
              )}
            </ul>

            {!selectedAccountFiles.application_form_filename &&
              !selectedAccountFiles.application_form_crawling &&
              !selectedAccountFiles.company_profile_filename &&
              !selectedAccountFiles.company_profile_crawling &&
              !selectedAccountFiles.financial_statement_filename &&
              !selectedAccountFiles.financial_statement_crawling &&
              !selectedAccountFiles.other_file_filename && (
                <Typography variant="body2" color="text.secondary">
                  No files available.
                </Typography>
              )}
          </DialogContent>
        </Dialog>
      )} */}
    </Stack>
  );
};

export default AccountTable;
